import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Stack, Typography, Divider, useMediaQuery,
  useTheme, Snackbar, Alert
} from '@mui/material';
import { updateCompanyProject } from '../../../helper/organizationsApis';

const EditProjectDialog = ({ open, onClose, project, onUpdate }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [form, setForm] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (project) {
      setForm({ ...project });
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      const { status, ...cleanedForm } = form; // Remove status from form data
      const response = await updateCompanyProject(project._id, cleanedForm);
      onUpdate(response.data);
      setSnackbar({ open: true, message: 'Project updated successfully!', severity: 'success' });
      onClose();
    } catch (error) {
     setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Update failed. Please try again.',
        severity: 'error',
      });
    }
  };

  if (!project) return null;

  return (
     <>
          <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
            <DialogTitle>Edit Project Details</DialogTitle>
            <DialogContent>
              <Stack spacing={3} mt={2}>
                {/* Section 1: Company Info */}
                <Typography variant="h6">Company Info</Typography>
                <Divider />
                <TextField label="Company Name" name="companyName" value={form.companyName || ''} InputProps={{ readOnly: true }} fullWidth required />
                <TextField label="Company Overview" name="companyOverview" value={form.companyOverview || ''} onChange={handleChange} fullWidth />
                <TextField label="Website or LinkedIn" name="websiteOrLinkedIn" value={form.websiteOrLinkedIn || ''} onChange={handleChange} fullWidth />
    
                {/* Section 2: General Info */}
                <Typography variant="h6">General Info</Typography>
                <Divider />
                <TextField label="Project Title" name="projectTitle" value={form.projectTitle || ''} onChange={handleChange} fullWidth />
                <TextField label="Project Summary" name="projectSummary" value={form.projectSummary || ''} onChange={handleChange} multiline rows={3} fullWidth />
                <TextField label="Domain" name="domain" value={form.domain || ''} onChange={handleChange} fullWidth />
                <TextField label="Preferred Tech Stack" name="preferredTechStack" value={form.preferredTechStack || ''} onChange={handleChange} fullWidth />
                <TextField label="Preferred Programming Languages" name="preferredProgrammingLanguages" value={form.preferredProgrammingLanguages || ''} onChange={handleChange} fullWidth />
                <TextField label="Expected Deliverables" name="expectedDeliverables" value={form.expectedDeliverables || ''} onChange={handleChange} fullWidth />
    
                {/* Section 3: Timeline */}
                <Typography variant="h6">Timeline</Typography>
                <Divider />
                <TextField label="Project Duration" name="projectDuration" value={form.projectDuration || ''} onChange={handleChange} fullWidth />
                <TextField label="Weekly Time Commitment" name="weeklyTimeCommitment" value={form.weeklyTimeCommitment || ''} onChange={handleChange} fullWidth />
                <TextField label="Mode of Work" name="modeOfWork" value={form.modeOfWork || ''} onChange={handleChange} fullWidth />
    
                {/* Section 4: Legal & Compensation */}
                <Typography variant="h6">Legal & Compensation</Typography>
                <Divider />
                <TextField label="Compensation" name="compensation" value={form.compensation || ''} onChange={handleChange} fullWidth />
                <TextField label="Confidentiality / IP Clauses" name="confidentialityOrIPClauses" value={form.confidentialityOrIPClauses || ''} onChange={handleChange} fullWidth />
    
                {/* Section 5: Optional Dates */}
                <Typography variant="h6">Dates</Typography>
                <Divider />
                <TextField
                  label="Desired Start Date"
                  name="desiredStartDate"
                  type="date"
                  value={form.desiredStartDate?.split('T')[0] || ''}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Application Deadline"
                  name="applicationDeadline"
                  type="date"
                  value={form.applicationDeadline?.split('T')[0] || ''}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
    
                {/* Section 6: Point of Contact */}
                <Typography variant="h6">Point of Contact</Typography>
                <Divider />
                <TextField label="Name" name="pointOfContact.name" value={form.pointOfContact?.name || ''} onChange={handleChange} fullWidth />
                <TextField label="Designation" name="pointOfContact.designation" value={form.pointOfContact?.designation || ''} onChange={handleChange} fullWidth />
                <TextField label="Email" name="pointOfContact.email" value={form.pointOfContact?.email || ''} onChange={handleChange} fullWidth />
                <TextField label="Phone Number" name="pointOfContact.phoneNumber" value={form.pointOfContact?.phoneNumber || ''} onChange={handleChange} fullWidth />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} variant="outlined">Cancel</Button>
              <Button onClick={handleSubmit} variant="contained" color="primary">Save</Button>
            </DialogActions>
          </Dialog>
    
          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width : '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
  );
};

export default EditProjectDialog;
